<script setup>
import { ref, shallowRef, onMounted, computed, provide } from 'vue'
import { useRoute } from 'vue-router'
import { getDetail } from '@/api/image'
import { getViewComponent, isSupportedTemplate } from './viewTypeMap'
import { normalizeAlgorithmPublicAssetUrls } from '@/utils/algorithmPublicAssets'
import {
  isChunkLoadError,
  recoverFromChunkLoadError,
} from '@/utils/chunkRecovery'
import Header from '@/components/Header.vue'
import DesignFooter from '@/components/DesignFooter.vue'
import './detail-redesign.scss'

const route = useRoute()
const algorithmType = computed(
  () => route.params.algorithmType || route.path.split('/').pop(),
)
const loading = ref(true)
const error = ref(null)
const detailData = ref(null)
const viewComponent = shallowRef(null)
const recoveringResources = ref(false)
const reloadPage = () => window.location.reload()

// 下划线转驼峰，用于匹配 contant.js 中的 key
// face_detection → faceDetection
function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

// 提供 algorithmType 和 detailData 给子组件
provide('algorithmType', algorithmType)
provide('detailData', detailData)
provide('algorithmDetailRedesign', true)

onMounted(async () => {
  try {
    loading.value = true
    error.value = null

    // 获取算法详情数据
    const requestId = Array.isArray(route.query.id)
      ? route.query.id[0]
      : route.query.id || algorithmType.value
    const res = await getDetail(requestId)
    detailData.value = normalizeAlgorithmPublicAssetUrls(
      res || {},
      res?.id || requestId,
    )

    // 从 introduce.template 获取模板类型
    const template =
      detailData.value.introduce?.template || 'general_recognition'

    // 检查 template 是否支持
    if (!isSupportedTemplate(template)) {
      console.warn(`Unsupported template: ${template}`)
    }

    // 动态导入对应的组件
    viewComponent.value = await getViewComponent(template)
  } catch (err) {
    console.error('Failed to load algorithm detail:', err)
    if (isChunkLoadError(err)) {
      if (recoverFromChunkLoadError(err)) {
        recoveringResources.value = true
        return
      }
      error.value = '页面资源已更新或暂时不可用，请刷新页面后重试。'
    } else {
      error.value = err.message || '加载算法详情失败'
    }
  } finally {
    if (!recoveringResources.value) loading.value = false
  }
})
</script>

<template>
  <Header design-always hide-banner />
  <div id="algorithm-detail-page" class="algorithm-detail-container">
    <div
      v-if="loading"
      class="loading"
      role="status"
      aria-label="算法详情加载中"
    >
      <a-spin size="large" />
    </div>

    <div v-else-if="error" class="error">
      <a-result status="error" title="加载失败" :sub-title="error">
        <template #extra>
          <a-button type="primary" @click="reloadPage">刷新页面</a-button>
        </template>
      </a-result>
    </div>

    <!-- 动态渲染对应的组件 -->
    <main v-else class="algorithm-template">
      <component :is="viewComponent" />
    </main>
  </div>
  <DesignFooter />
</template>

<style scoped>
.algorithm-detail-container {
  width: 100%;
  min-height: 100vh;
}

.loading,
.error {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
}
</style>
